import type { ThemeConfig } from 'antd';
import { theme } from 'antd';

const composedExtensions = new WeakMap<
  typeof theme.defaultAlgorithm,
  (typeof theme.defaultAlgorithm)[]
>();

/** A scalar derivative avoids Umi's deep-merge retaining old algorithm array entries. */
export function platformTheme(
  previous: ThemeConfig = {},
  dark: boolean,
): ThemeConfig {
  const algorithms = Array.isArray(previous.algorithm)
    ? previous.algorithm
    : previous.algorithm
      ? [previous.algorithm]
      : [];
  const extensions = algorithms
    .flatMap((fn) => composedExtensions.get(fn) ?? [fn])
    .filter(
      (fn) => fn !== theme.darkAlgorithm && fn !== theme.defaultAlgorithm,
    );
  const base = dark ? theme.darkAlgorithm : theme.defaultAlgorithm;
  const algorithm: typeof base = (seed, map) =>
    extensions.reduce((tokens, fn) => fn(seed, tokens), base(seed, map));
  composedExtensions.set(algorithm, extensions);
  return {
    ...previous,
    algorithm,
    cssVar: {
      ...(typeof previous.cssVar === 'object' ? previous.cssVar : {}),
      key: dark ? 'platform-dark' : 'platform-light',
    },
    components: {
      ...previous.components,
      Button: {
        ...previous.components?.Button,
        defaultShadow: 'none',
        primaryShadow: 'none',
        dangerShadow: 'none',
        contentFontSizeSM: 13,
        paddingInlineSM: 10,
        paddingBlockSM: 0,
        fontWeight: 500,
        controlHeight: 32,
      },
      Table: {
        ...previous.components?.Table,
        headerBg: dark ? '#1f1f1f' : '#f7f8fa',
        headerColor: dark ? 'rgba(255, 255, 255, 0.88)' : '#1f2633',
        headerSplitColor: 'transparent',
        rowHoverBg: dark ? '#262626' : '#fafafa',
        borderColor: dark ? '#303030' : '#e4e8ef',
        colorBgContainer: dark ? '#141414' : '#ffffff',
      },
      Card: {
        ...previous.components?.Card,
        colorBgContainer: dark ? '#141414' : '#ffffff',
        headerBg: 'transparent',
      },
      Input: {
        ...previous.components?.Input,
        controlHeight: 32,
      },
      Select: {
        ...previous.components?.Select,
        controlHeight: 32,
      },
      DatePicker: {
        ...previous.components?.DatePicker,
        controlHeight: 32,
      },
      InputNumber: {
        ...previous.components?.InputNumber,
        controlHeight: 32,
      },
    },
  };
}
